/**
 * The selected job comes from the route (`/t/:chain/:token`, `/liquidity/:chain`,
 * `/hooks/:chain`); this hook resolves it against /api/jobs.
 */
import { useEffect } from "react";
import { useParams } from "react-router";
import type { ChainJob, TokenJob } from "@/api/types";
import { useJobs } from "@/api/hooks";
import { hooksJobId, liquidityJobId, tokenJobId } from "@/api/types";
import { useUi } from "./ui";

export interface CurrentJob {
  kind: "token" | "liquidity" | "hooks" | "none";
  id?: string;
  chain?: string;
  token?: string;
  tokenJob?: TokenJob;
  chainJob?: ChainJob;
  isLoading: boolean;
}

export function useCurrentJob(): CurrentJob {
  const { chain, token } = useParams<{ chain?: string; token?: string }>();
  const path = typeof location !== "undefined" ? location.pathname : "";
  const kind: CurrentJob["kind"] =
    chain && token
      ? "token"
      : path.startsWith("/liquidity/") && chain
        ? "liquidity"
        : path.startsWith("/hooks/") && chain
          ? "hooks"
          : "none";
  const jobs = useJobs();
  const setLastJob = useUi((s) => s.setLastJob);

  let id: string | undefined;
  let tokenJob: TokenJob | undefined;
  let chainJob: ChainJob | undefined;
  if (kind === "token" && chain && token) {
    id = tokenJobId(chain, token);
    tokenJob = jobs.data?.data.tokens.find((j) => j.id === id);
  } else if (kind === "liquidity" && chain) {
    id = liquidityJobId(chain);
    chainJob = jobs.data?.data.liquidity.find((j) => j.id === id);
  } else if (kind === "hooks" && chain) {
    id = hooksJobId(chain);
    chainJob = jobs.data?.data.hooks.find((j) => j.id === id);
  }

  useEffect(() => {
    if (id) setLastJob(id);
  }, [id, setLastJob]);

  return {
    kind,
    id,
    chain: chain?.toLowerCase(),
    token: token?.toLowerCase(),
    tokenJob,
    chainJob,
    isLoading: jobs.isLoading,
  };
}
